import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

// Configurações → Servidor: como está o servidor agora (mesmos números do painel
// resumo.gestaoquality.com.br, lidos por /api/servidor-status). Atualiza a cada 5s só
// enquanto a aba do navegador está visível.
const VERDE = '#16a34a', AMARELO = '#d97706', VERMELHO = '#dc2626';
const corPara = pct => (pct < 70 ? VERDE : pct < 90 ? AMARELO : VERMELHO);

const gb = mb => (mb / 1024).toFixed(1);
const kbps = k => (k < 1024 ? `${k.toFixed(0)} Kbps` : `${(k / 1024).toFixed(1)} Mbps`);
const kbs = k => (k < 1024 ? `${k.toFixed(0)} KB/s` : `${(k / 1024).toFixed(1)} MB/s`);
const tempoLigado = s => {
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h ${Math.floor((s % 3600) / 60)}m`;
};

const cartao = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '18px 20px', textAlign: 'center' };
const rotulo = { fontSize: '0.82rem', color: '#6b7280', marginBottom: 6 };
const numero = { fontSize: '1.9rem', fontWeight: 800, color: '#111827', lineHeight: 1.1 };
const detalhe = { fontSize: '0.8rem', color: '#6b7280', marginTop: 6 };

function Barra({ pct }) {
  return (
    <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, marginTop: 12, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: corPara(pct), transition: 'width .4s' }} />
    </div>
  );
}

export default function ServidorStatus() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let ativo = true;
    const buscar = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const r = await fetch('/api/servidor-status', { headers: { Authorization: `Bearer ${session?.access_token || ''}` } });
        const j = await r.json().catch(() => ({}));
        if (!ativo) return;
        if (!r.ok) { setErro(j.error || `Erro ${r.status}`); return; }
        setDados(j);
        setErro('');
      } catch (e) {
        if (ativo) setErro('Não consegui buscar os dados agora (tentando de novo...)');
      }
    };
    buscar();
    const timer = setInterval(buscar, 5000);
    document.addEventListener('visibilitychange', buscar);
    return () => { ativo = false; clearInterval(timer); document.removeEventListener('visibilitychange', buscar); };
  }, []);

  if (!dados) {
    return <div className="tab-content" style={{ padding: 24, color: erro ? VERMELHO : '#6b7280' }}>{erro || 'Carregando…'}</div>;
  }

  const ramPct = dados.ram.totalMB ? (dados.ram.usadoMB / dados.ram.totalMB) * 100 : 0;
  const discoPct = dados.disco.totalGB ? (dados.disco.usadoGB / dados.disco.totalGB) * 100 : 0;
  const pior = Math.max(dados.cpu, ramPct, discoPct);
  const aviso = erro
    ? { texto: `❌ ${erro}`, fundo: '#f3f4f6', cor: '#4b5563' }
    : pior < 70 ? { texto: '✅ Tudo certo — servidor tranquilo', fundo: '#dcfce7', cor: '#14532d' }
    : pior < 90 ? { texto: '⚠️ Uso elevado em algum item — vale acompanhar', fundo: '#fef3c7', cor: '#78350f' }
    : { texto: '🔴 Uso muito alto — atenção, pode precisar de ação', fundo: '#fee2e2', cor: '#7f1d1d' };

  return (
    <div className="tab-content" style={{ padding: 20, maxWidth: 1100 }}>
      <h3 style={{ margin: '0 0 4px' }}>🖥️ Como está o servidor agora</h3>
      <p style={{ margin: '0 0 16px', fontSize: '0.82rem', color: '#6b7280' }}>
        quality-servidor-01 · atualiza sozinho a cada 5 segundos · última leitura {new Date(dados.em).toLocaleTimeString('pt-BR')}
      </p>

      <div style={{ background: aviso.fundo, color: aviso.cor, borderRadius: 12, padding: '14px 18px', fontWeight: 700, textAlign: 'center', marginBottom: 16 }}>
        {aviso.texto}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 22 }}>
        <div style={cartao}>
          <div style={rotulo}>Processador (CPU)</div>
          <div style={numero}>{dados.cpu.toFixed(0)}%</div>
          <Barra pct={dados.cpu} />
        </div>
        <div style={cartao}>
          <div style={rotulo}>Memória (RAM)</div>
          <div style={numero}>{gb(dados.ram.usadoMB)} GB</div>
          <div style={detalhe}>de {gb(dados.ram.totalMB)} GB totais ({ramPct.toFixed(0)}%)</div>
          <Barra pct={ramPct} />
        </div>
        <div style={cartao}>
          <div style={rotulo}>Disco (armazenamento)</div>
          <div style={numero}>{dados.disco.usadoGB.toFixed(1)} GB</div>
          <div style={detalhe}>de {dados.disco.totalGB.toFixed(0)} GB totais ({discoPct.toFixed(0)}%)</div>
          <Barra pct={discoPct} />
        </div>
      </div>

      <h4 style={{ margin: '0 0 10px', color: '#374151' }}>Rede, disco e tempo ligado</h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 22 }}>
        {[
          ['Internet — baixando', kbps(dados.rede.recebendoKbit)],
          ['Internet — enviando', kbps(dados.rede.enviandoKbit)],
          ['Disco — lendo', kbs(dados.io.leituraKB)],
          ['Disco — gravando', kbs(dados.io.escritaKB)],
          ['Tempo ligado sem reiniciar', tempoLigado(dados.ligadoSeg)],
        ].map(([r, v]) => (
          <div key={r} style={cartao}>
            <div style={rotulo}>{r}</div>
            <div style={{ ...numero, fontSize: '1.4rem' }}>{v}</div>
          </div>
        ))}
      </div>

      <h4 style={{ margin: '0 0 10px', color: '#374151' }}>Consumo por serviço (quem está usando o quê)</h4>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb', color: '#6b7280', textAlign: 'left' }}>
              <th style={{ padding: '10px 14px' }}>Serviço</th>
              <th style={{ padding: '10px 14px', textAlign: 'right' }}>CPU</th>
              <th style={{ padding: '10px 14px', textAlign: 'right' }}>Memória</th>
            </tr>
          </thead>
          <tbody>
            {dados.servicos.length === 0 && (
              <tr><td colSpan={3} style={{ padding: 14, color: '#6b7280' }}>Sem dados por serviço no momento.</td></tr>
            )}
            {dados.servicos.map((s, i) => (
              <tr key={`${s.nome}-${i}`} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: '9px 14px' }}>{s.nome}</td>
                {/* referência: 25% de 1 vCPU já pinta amarelo */}
                <td style={{ padding: '9px 14px', textAlign: 'right', color: s.cpu == null ? '#9ca3af' : corPara(s.cpu * 4), fontWeight: 600 }}>
                  {s.cpu == null ? '—' : `${s.cpu.toFixed(1)}%`}
                </td>
                <td style={{ padding: '9px 14px', textAlign: 'right' }}>
                  {s.ramMB < 1024 ? `${s.ramMB.toFixed(0)} MB` : `${(s.ramMB / 1024).toFixed(2)} GB`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
